// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// In-clinic kiosk entry. The provider hands the tablet off with a single-use lock
// token (?kt=), minted server-side via POST /v1/kiosk/start. We redeem it once for
// a patient session (POST /v1/kiosk/redeem) — the token IS the authorization, so
// there is NO DOB/OTP step (provider_authorized). No PHI is persisted; the first
// name lives in memory only for the greeting. A bad/expired/used token routes to
// the expired-link screen.

import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Lock, Mic, ShieldCheck, Hand, Loader2 } from 'lucide-react'
import { Logo } from '@/components/Logo'
import { Branding } from '@qh/design-tokens/branding'
import { enterKioskMode, lockKioskNavigation, setKioskSession, setKioskReturnUrl } from '@/lib/kiosk-mode'
import { redeemKiosk } from '@/intake/intake-service'
import { setSession, setPatientFirstName, getPatientFirstName, hasValidSession, clearSession, setKioskLockToken, getKioskLockToken } from '@/lib/session'
import { useLanguage } from '@/i18n/use-language'

const BULLET_ICONS = [Mic, ShieldCheck, Hand] as const

export function KioskWelcomeScreen() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { setLanguage } = useLanguage()
  const [searchParams] = useSearchParams()
  const lockToken = searchParams.get('kt') ?? ''
  const returnUrl = searchParams.get('return') ?? ''

  const [phase, setPhase] = useState<'redeeming' | 'ready'>('redeeming')
  const [firstName, setFirstName] = useState('')
  const [fullscreenError, setFullscreenError] = useState(false)

  // Redeem exactly once. The lock token is SINGLE-USE, so neither a same-mount
  // double-invoke (useRef guard) nor a remount/reload (e.g. the SW self-destruct
  // reload, or the patient refreshing) may re-redeem a consumed token — that 401s
  // and bounces an already-valid session to /link-expired. So if a session already
  // exists we skip redemption entirely and proceed.
  const redeemed = useRef(false)
  useEffect(() => {
    if (redeemed.current) return
    redeemed.current = true
    // Remember where the provider tablet should return after staff unlock (the
    // provider-admin page that launched this kiosk). Captured once at entry.
    if (returnUrl) setKioskReturnUrl(returnUrl)
    // Re-mount of the SAME already-consumed token (StrictMode double-mount, back-nav):
    // the single-use token is spent, so don't re-redeem — go straight in.
    if (hasValidSession() && lockToken && getKioskLockToken() === lockToken) {
      lockKioskNavigation()
      setKioskSession()
      setFirstName(getPatientFirstName() ?? '')
      setPhase('ready')
      return
    }
    if (!lockToken) {
      navigate('/link-expired', { replace: true })
      return
    }
    // A genuine new hand-off (different token, or none before): wipe any prior
    // patient's/encounter's session so the tablet can NEVER inherit a stale
    // interaction/conversation, then redeem the new token clean.
    clearSession()
    redeemKiosk(lockToken)
      .then(res => {
        setSession(res.sessionJwt)
        setKioskLockToken(lockToken)
        setPatientFirstName(res.patientFirstName)
        setLanguage(res.locale)
        lockKioskNavigation()
        setKioskSession()
        setFirstName(res.patientFirstName)
        setPhase('ready')
      })
      .catch(() => {
        navigate('/link-expired', { replace: true })
      })
  }, [lockToken, returnUrl, navigate, setLanguage])

  async function handleStart() {
    setFullscreenError(false)
    const fullscreen = await enterKioskMode()
    if (!fullscreen) {
      setFullscreenError(true)
      return
    }
    setKioskSession()
    navigate('/kiosk/consent', { replace: true })
  }

  const bullets = BULLET_ICONS.map((Icon, i) => ({ Icon, text: t(`kiosk.bullet${i + 1}`) }))
  const greeting = firstName ? t('kiosk.greetingName', { name: firstName }) : t('kiosk.greeting')

  return (
    <div className="min-h-dvh flex flex-col bg-gradient-to-br from-[#011829] via-[#012540] to-[#011829] relative overflow-hidden">
      {/* Ambient glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-24 left-[10%] w-[500px] h-[500px] rounded-full bg-[#006EF9]/10 blur-[140px]" />
        <div className="absolute bottom-[5%] right-[-5%] w-[400px] h-[400px] rounded-full bg-[#02C7C3]/8 blur-[120px]" />
      </div>

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 pt-10 pb-2 sm:px-10">
        <div style={{ filter: 'brightness(0) invert(1)' }}>
          <Logo variant="mark" className="h-7 w-auto" aria-hidden />
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#02C7C3]/15 border border-[#02C7C3]/30">
          <Lock className="h-3 w-3 text-[#02C7C3]" />
          <span className="text-[11px] font-semibold text-[#02C7C3] uppercase tracking-widest">
            {t('kiosk.lockedBadge')}
          </span>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col flex-1 items-center justify-center px-6 py-10 sm:px-10">
        {phase === 'redeeming' ? (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 text-[#02C7C3] animate-spin" />
            <p className="text-white/70 text-sm">{t('kiosk.preparing')}</p>
          </div>
        ) : (
          <div className="w-full max-w-sm flex flex-col">
            {/* Greeting */}
            <p className="text-[#02C7C3] text-sm font-semibold mb-1">{t('kiosk.tagline')}</p>
            <h1 className="text-[40px] sm:text-5xl font-bold text-white leading-tight mb-6">
              {greeting}
            </h1>

            {/* Body */}
            <p className="text-white/75 text-[16px] leading-relaxed mb-8">
              {t('kiosk.body')}
            </p>

            {/* Bullets */}
            <div className="flex flex-col gap-3 mb-10">
              {bullets.map(({ Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-xl bg-white/8 border border-white/12 flex items-center justify-center shrink-0">
                    <Icon className="h-4 w-4 text-[#02C7C3]" />
                  </div>
                  <span className="text-white/70 text-[14px]">{text}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <button
              onClick={() => void handleStart()}
              className="w-full h-14 rounded-2xl font-bold text-base text-white bg-gradient-to-r from-[#006EF9] to-[#02C7C3] shadow-[0_0_40px_rgba(0,110,249,0.35)] hover:shadow-[0_0_60px_rgba(2,199,195,0.45)] hover:scale-[1.02] active:scale-95 transition-all duration-200"
            >
              {t('kiosk.startButton')}
            </button>
            {fullscreenError && (
              <p role="alert" className="mt-3 text-center text-sm text-[#FFB4A8]">
                {t('kiosk.fullscreenRequired')}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Bottom HIPAA note */}
      <div className="relative z-10 flex flex-col items-center gap-2 px-6 pb-6">
        <div className="flex items-center justify-center gap-1.5">
          <ShieldCheck className="h-3 w-3 text-white/25" />
          <p className="text-white/25 text-xs">{t('kiosk.hipaaNote')}</p>
        </div>
        <Branding variant="inverse" />
      </div>
    </div>
  )
}
